// Centralized logging utility with environment-based gating

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  isDevelopment: boolean;
}

const config: LoggerConfig = {
  enabled: true,
  level: import.meta.env.MODE === 'production' ? 'warn' : 'debug',
  isDevelopment: import.meta.env.MODE !== 'production',
};

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const shouldLog = (level: LogLevel): boolean => {
  if (!config.enabled) return false;
  return LOG_LEVELS[level] >= LOG_LEVELS[config.level];
};

const formatMessage = (level: LogLevel, message: string, ...args: unknown[]): [string, ...unknown[]] => {
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
  return [`${prefix} ${message}`, ...args];
};

export const logger = {
  debug: (message: string, ...args: unknown[]) => {
    if (shouldLog('debug')) {
      console.log(...formatMessage('debug', message, ...args));
    }
  },

  info: (message: string, ...args: unknown[]) => {
    if (shouldLog('info')) {
      console.info(...formatMessage('info', message, ...args));
    }
  },

  warn: (message: string, ...args: unknown[]) => {
    if (shouldLog('warn')) {
      console.warn(...formatMessage('warn', message, ...args));
    }
  },

  error: (message: string, ...args: unknown[]) => {
    if (shouldLog('error')) {
      console.error(...formatMessage('error', message, ...args));
    }
  },

  // Group logging for related messages
  group: (label: string) => {
    if (config.isDevelopment) {
      console.group(label);
    }
  },

  groupEnd: () => {
    if (config.isDevelopment) {
      console.groupEnd();
    }
  },
};
